/**
 * ChatToggle Component - Breadcrumb Toggle Button
 *
 * A toggle button that appears in the breadcrumb bar to open/close
 * the discussion overlay. Shows unread count badge (Phase 2).
 *
 * Features:
 * - Chat bubble icon
 * - Active state indication (blue when open)
 * - Unread badge (future enhancement)
 * - Tooltip for accessibility
 * - Responsive sizing
 */

import React from 'react';
import { IconButton, Badge, Tooltip } from '@mui/material';
import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ChatBubble as ChatBubbleIcon
} from '@mui/icons-material';

/**
 * ChatToggle Component
 *
 * @param {Object} props
 * @param {Function} props.onClick - Callback when button clicked
 * @param {boolean} [props.isOpen=false] - Whether discussion overlay is open
 * @param {number} [props.unreadCount=0] - Number of unread messages (Phase 2)
 * @param {string} [props.label] - Optional text label to display next to icon
 */
const ChatToggle = ({ onClick, isOpen = false, unreadCount = 0, label }) => {
  // If no label, render as IconButton (original behavior)
  if (!label) {
    return (
      <Tooltip title="Discussion Group" arrow>
        <IconButton
          onClick={onClick}
          size="small"
          sx={{
            color: isOpen ? 'primary.main' : 'text.secondary',
            marginLeft: 1,
            transition: 'all 0.2s',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.625rem',
                height: '16px',
                minWidth: '16px',
                padding: '0 4px'
              }
            }}
          >
            {isOpen ? (
              <ChatBubbleIcon sx={{ fontSize: '1.25rem' }} />
            ) : (
              <ChatBubbleOutlineIcon sx={{ fontSize: '1.25rem' }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>
    );
  }

  // With label, render as full button with icon + text
  return (
    <Tooltip title="Discussion Group" arrow>
      <button
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 8px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: isOpen ? '#1976d2' : '#666',
          fontSize: '0.95rem',
          fontWeight: 400,
          transition: 'all 0.2s',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          fontFamily: 'inherit'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f4ff';
          e.currentTarget.style.color = '#1976d2';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = isOpen ? '#1976d2' : '#666';
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.625rem',
              height: '16px',
              minWidth: '16px',
              padding: '0 4px'
            }
          }}
        >
          {isOpen ? (
            <ChatBubbleIcon sx={{ fontSize: '1.25rem' }} />
          ) : (
            <ChatBubbleOutlineIcon sx={{ fontSize: '1.25rem' }} />
          )}
        </Badge>
        <span>{label}</span>
      </button>
    </Tooltip>
  );
};

export default ChatToggle;
